import { trackEvent, EVENTS } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

interface StreamChatOptions {
  message: string;
  activeVendors: string[];
  onDelta: (text: string) => void;
  onIntents: (intents: string[]) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

export async function streamChatMessage({
  message,
  activeVendors,
  onDelta,
  onIntents,
  onDone,
  onError,
}: StreamChatOptions): Promise<void> {
  trackEvent(EVENTS.CHAT_MESSAGE, { vendors: activeVendors });
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;

  // Get the user's actual session token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ message, activeVendors }),
  });

  if (!resp.ok) {
    if (resp.status === 429) {
      onError("Rate limit exceeded. Please try again shortly.");
      return;
    }
    if (resp.status === 402) {
      onError("AI credits exhausted. Please add funds.");
      return;
    }
    // Try to parse JSON error
    try {
      const errData = await resp.json();
      onError(errData.error || "Failed to get response.");
    } catch {
      onError("Failed to get response. Please try again.");
    }
    return;
  }

  if (!resp.body) {
    onError("No response body received.");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
      let line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr);

        // Check if this is our custom intents metadata event
        if (parsed.intents && !parsed.choices) {
          onIntents(parsed.intents);
          continue;
        }

        // Standard SSE delta
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {
        // Incomplete JSON — put back and wait for more data
        buffer = line + "\n" + buffer;
        break;
      }
    }
  }

  // Final flush
  if (buffer.trim()) {
    for (let raw of buffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "") continue;
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        if (parsed.intents && !parsed.choices) {
          onIntents(parsed.intents);
          continue;
        }
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch { /* ignore */ }
    }
  }

  onDone();
}
